
import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { PasswordResetRedirect } from "@/components/PasswordResetRedirect";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { LOADING_MESSAGES } from "@/constants/loadingMessages";
import MaintenanceGuard from "@/components/maintenance/MaintenanceGuard";

// Critical routes - loaded immediately for fastest initial page load
import Index from "@/pages/Index";
import NotFound from "@/pages/NotFound";

// Lazy load non-critical routes to reduce main bundle size and improve TTI
const FindPlaces = lazy(() => import("@/pages/FindPlaces"));
const BusinessDetail = lazy(() => import("@/pages/BusinessDetail"));
const RatePlace = lazy(() => import("@/pages/RatePlace"));
const Auth = lazy(() => import("@/pages/Auth"));
const Profile = lazy(() => import("@/pages/Profile"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminBusinesses = lazy(() => import("@/pages/AdminBusinesses"));
const AdminBusinessEdit = lazy(() => import("@/pages/AdminBusinessEdit"));
const AdminRatings = lazy(() => import("@/pages/AdminRatings"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminImport = lazy(() => import("@/pages/AdminImport"));
const AdminAnalyticsAds = lazy(() => import("@/pages/AdminAnalyticsAds"));
const AdminMaintenance = lazy(() => import("@/pages/AdminMaintenance"));
const AdminEditPages = lazy(() => import("@/pages/AdminEditPages"));
const AdminBenchmark = lazy(() => import("@/pages/AdminBenchmark"));
const EmailConfirmation = lazy(() => import("@/pages/EmailConfirmation"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Contact = lazy(() => import("@/pages/Contact"));
const About = lazy(() => import("@/pages/About"));
const Favorites = lazy(() => import("@/pages/Favorites"));

// Optimized loading fallback
const PageLoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <LoadingSpinner message={LOADING_MESSAGES.PAGE} />
  </div>
);

export default function AppRoutes() {
  return (
    <>
      <PasswordResetRedirect />
      <Routes>
        {/* Public routes with maintenance guard */}
        <Route path="/" element={<MaintenanceGuard><Index /></MaintenanceGuard>} />
        <Route path="/find-places" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <FindPlaces />
            </Suspense>
          </MaintenanceGuard>
        } />
        <Route path="/business/:id" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <BusinessDetail />
            </Suspense>
          </MaintenanceGuard>
        } />
        <Route path="/rate-place" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <RatePlace />
            </Suspense>
          </MaintenanceGuard>
        } />
        {/* Auth routes - NO maintenance guard */}
        <Route path="/auth" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <Auth />
          </Suspense>
        } />
        <Route path="/email-confirmation" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <EmailConfirmation />
          </Suspense>
        } />
        <Route path="/reset-password" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <ResetPassword />
          </Suspense>
        } />
        <Route path="/profile" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <Profile />
            </Suspense>
          </MaintenanceGuard>
        } />
        <Route path="/favorites" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <Favorites />
            </Suspense>
          </MaintenanceGuard>
        } />
        {/* Admin routes - NO maintenance guard */}
        <Route path="/admin" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <Admin />
          </Suspense>
        } />
        <Route path="/admin/businesses" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminBusinesses />
          </Suspense>
        } />
        <Route path="/admin/businesses/new" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminBusinessEdit />
          </Suspense>
        } />
        <Route path="/admin/businesses/:id" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminBusinessEdit />
          </Suspense>
        } />
        <Route path="/admin/ratings" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminRatings />
          </Suspense>
        } />
        <Route path="/admin/reviews" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminReviews />
          </Suspense>
        } />
        <Route path="/admin/users" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminUsers />
          </Suspense>
        } />
        <Route path="/admin/import" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminImport />
          </Suspense>
        } />
        <Route path="/admin/analytics-ads" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminAnalyticsAds />
          </Suspense>
        } />
        <Route path="/admin/maintenance" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminMaintenance />
          </Suspense>
        } />
        <Route path="/admin/edit-pages" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminEditPages />
          </Suspense>
        } />
        <Route path="/admin/benchmark" element={
          <Suspense fallback={<PageLoadingFallback />}>
            <AdminBenchmark />
          </Suspense>
        } />
        {/* Static pages with maintenance guard */}
        <Route path="/terms" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <Terms />
            </Suspense>
          </MaintenanceGuard>
        } />
        <Route path="/privacy" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <Privacy />
            </Suspense>
          </MaintenanceGuard>
        } />
        <Route path="/contact" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <Contact />
            </Suspense>
          </MaintenanceGuard>
        } />
        <Route path="/about" element={
          <MaintenanceGuard>
            <Suspense fallback={<PageLoadingFallback />}>
              <About />
            </Suspense>
          </MaintenanceGuard>
        } />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
